/* lang-connected.js — connected-speech model for Timbrica's English tools.
 *
 * Three things a serious pronunciation tool needs, which pages otherwise
 * hard-code separately (and differently) each time:
 *
 *   1. classify(tokens)  — content vs function word, POS-aware where the same
 *      spelling is both. "That is the book that I read" has a DEMONSTRATIVE
 *      that (stressed, /ðæt/) and a relativiser that (weak, /ðət/); a flat
 *      closed-class list calls both function words and teaches the wrong thing.
 *
 *   2. weakForm(word, i, tokens, accent) — the reduced pronunciation a function
 *      word actually takes in running speech, INCLUDING the positions where the
 *      strong form is obligatory: utterance-final, before a pause, before a
 *      vowel (to/the), stranded auxiliary ("Yes, I can."). toPhonetics ships a
 *      "show weak forms" checkbox; the positional rules are what make it right.
 *
 *   3. groups(tokens) — thought groups and the NUCLEUS of each: the one word
 *      carrying the main sentence stress. Default tonicity is the last content
 *      word of the group, with the two exceptions every course teaches
 *      (sentence-final phrasal-verb particle takes it; a final empty/deictic
 *      pro-form hands it back to the word before).
 *
 * Depends on nothing. Consumed by /sentence-stress and /text-to-ipa.
 * UMD: window.LangConnected + module.exports.
 */
(function (root, factory) {
  'use strict';
  var LC = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = LC;
  if (typeof window !== 'undefined') window.LangConnected = LC;
  else if (root) root.LangConnected = LC;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function set(s) { var o = Object.create(null); s.split(/\s+/).forEach(function (w) { if (w) o[w] = 1; }); return o; }
  function norm(t) { return String(t || '').toLowerCase().replace(/[^a-z’']/g, '').replace(/’/g, "'"); }

  /* ── Closed classes ──────────────────────────────────────────────────────
   * Function words are unstressed in connected speech *by default*. Members
   * that are also content words in another reading are resolved in classify().
   */
  var ARTICLE  = set('a an the');
  var PREP     = set('of to in on at by for with from as into onto over under ' +
                     'about after before during through against without within ' +
                     'between among across than upon per towards toward');
  var CONJ     = set("and but or nor so yet if because while when where whether " +
                     'since unless until though although as that');
  var PRONOUN  = set("i you he she it we they me him her us them my your his its " +
                     "our their mine yours hers ours theirs myself yourself himself " +
                     "herself itself ourselves themselves");
  var AUX      = set("am is are was were be been being do does did have has had " +
                     "can could shall should will would may might must ought " +
                     "'m 're 's 've 'll 'd");
  var DET      = set('this that these those some any no each every another such ' +
                     'both either neither much many more most few little several all');
  var PARTICLE = set('up down off out in on away back over through along around');

  /* Never reduced, whatever their class: negation and wh-words carry meaning. */
  var ALWAYS_STRONG = set("not no never nothing none nobody nowhere neither nor " +
                          "what which who whom whose why how where when " +
                          "can't won't don't doesn't didn't isn't aren't wasn't " +
                          "weren't hasn't haven't hadn't couldn't shouldn't " +
                          "wouldn't mustn't shan't ain't cannot");

  /* Pro-forms too empty to carry the nucleus when they end a group:
   * "I don't LIKE it", not "I don't like IT".
   * Deliberately WITHOUT here/there and the demonstratives: those are deictic,
   * they point at something new, and they do take the accent — "Come HERE",
   * "Put it THERE", "Look at THAT", "Do you live HERE?". Listing them here
   * pushed the nucleus one word left in exactly the sentences where the place
   * is the point of the utterance. */
  var EMPTY_END = set("it them him her us me you one ones thing things something " +
                      "anything everything somebody anybody everybody");

  /* ── Weak forms (Cambridge / Wells). `w` = weak, `s` = strong. ────────────
   * Two accents because the differences are systematic, not cosmetic:
   * non-rhotic GB turns every final /ɚ/ into /ə/. */
  var WEAK = {
    a:      { us: { w: 'ə',    s: 'eɪ'   }, gb: { w: 'ə',    s: 'eɪ'   } },
    an:     { us: { w: 'ən',   s: 'æn'   }, gb: { w: 'ən',   s: 'æn'   } },
    the:    { us: { w: 'ðə',   s: 'ði'   }, gb: { w: 'ðə',   s: 'ðiː'  }, preVowel: { us: 'ði', gb: 'ði' } },
    and:    { us: { w: 'ən',   s: 'ænd'  }, gb: { w: 'ən',   s: 'ænd'  } },
    but:    { us: { w: 'bət',  s: 'bʌt'  }, gb: { w: 'bət',  s: 'bʌt'  } },
    or:     { us: { w: 'ɚ',    s: 'ɔr'   }, gb: { w: 'ə',    s: 'ɔː'   } },
    of:     { us: { w: 'əv',   s: 'ʌv'   }, gb: { w: 'əv',   s: 'ɒv'   } },
    to:     { us: { w: 'tə',   s: 'tu'   }, gb: { w: 'tə',   s: 'tuː'  }, preVowel: { us: 'tu', gb: 'tu' } },
    for:    { us: { w: 'fɚ',   s: 'fɔr'  }, gb: { w: 'fə',   s: 'fɔː'  } },
    from:   { us: { w: 'frəm', s: 'frʌm' }, gb: { w: 'frəm', s: 'frɒm' } },
    at:     { us: { w: 'ət',   s: 'æt'   }, gb: { w: 'ət',   s: 'æt'   } },
    as:     { us: { w: 'əz',   s: 'æz'   }, gb: { w: 'əz',   s: 'æz'   } },
    than:   { us: { w: 'ðən',  s: 'ðæn'  }, gb: { w: 'ðən',  s: 'ðæn'  } },
    that:   { us: { w: 'ðət',  s: 'ðæt'  }, gb: { w: 'ðət',  s: 'ðæt'  } },
    there:  { us: { w: 'ðɚ',   s: 'ðɛr'  }, gb: { w: 'ðə',   s: 'ðeə'  } },
    some:   { us: { w: 'səm',  s: 'sʌm'  }, gb: { w: 'səm',  s: 'sʌm'  } },
    am:     { us: { w: 'əm',   s: 'æm'   }, gb: { w: 'əm',   s: 'æm'   } },
    are:    { us: { w: 'ɚ',    s: 'ɑr'   }, gb: { w: 'ə',    s: 'ɑː'   } },
    was:    { us: { w: 'wəz',  s: 'wʌz'  }, gb: { w: 'wəz',  s: 'wɒz'  } },
    were:   { us: { w: 'wɚ',   s: 'wɝ'   }, gb: { w: 'wə',   s: 'wɜː'  } },
    be:     { us: { w: 'bi',   s: 'bi'   }, gb: { w: 'bi',   s: 'biː'  } },
    been:   { us: { w: 'bɪn',  s: 'bɪn'  }, gb: { w: 'bɪn',  s: 'biːn'  } },
    do:     { us: { w: 'də',   s: 'du'   }, gb: { w: 'də',   s: 'duː'  }, preVowel: { us: 'du', gb: 'du' } },
    does:   { us: { w: 'dəz',  s: 'dʌz'  }, gb: { w: 'dəz',  s: 'dʌz'  } },
    have:   { us: { w: 'həv',  s: 'hæv'  }, gb: { w: 'həv',  s: 'hæv'  } },
    has:    { us: { w: 'həz',  s: 'hæz'  }, gb: { w: 'həz',  s: 'hæz'  } },
    had:    { us: { w: 'həd',  s: 'hæd'  }, gb: { w: 'həd',  s: 'hæd'  } },
    can:    { us: { w: 'kən',  s: 'kæn'  }, gb: { w: 'kən',  s: 'kæn'  } },
    could:  { us: { w: 'kəd',  s: 'kʊd'  }, gb: { w: 'kəd',  s: 'kʊd'  } },
    shall:  { us: { w: 'ʃəl',  s: 'ʃæl'  }, gb: { w: 'ʃəl',  s: 'ʃæl'  } },
    should: { us: { w: 'ʃəd',  s: 'ʃʊd'  }, gb: { w: 'ʃəd',  s: 'ʃʊd'  } },
    will:   { us: { w: 'wəl',  s: 'wɪl'  }, gb: { w: 'wəl',  s: 'wɪl'  } },
    would:  { us: { w: 'wəd',  s: 'wʊd'  }, gb: { w: 'wəd',  s: 'wʊd'  } },
    must:   { us: { w: 'məst', s: 'mʌst' }, gb: { w: 'məst', s: 'mʌst' } },
    he:     { us: { w: 'i',    s: 'hi'   }, gb: { w: 'i',    s: 'hiː'  } },
    him:    { us: { w: 'ɪm',   s: 'hɪm'  }, gb: { w: 'ɪm',   s: 'hɪm'  } },
    his:    { us: { w: 'ɪz',   s: 'hɪz'  }, gb: { w: 'ɪz',   s: 'hɪz'  } },
    her:    { us: { w: 'ɚ',    s: 'hɝ'   }, gb: { w: 'ə',    s: 'hɜː'  } },
    them:   { us: { w: 'ðəm',  s: 'ðɛm'  }, gb: { w: 'ðəm',  s: 'ðem'  } },
    us:     { us: { w: 'əs',   s: 'ʌs'   }, gb: { w: 'əs',   s: 'ʌs'   } },
    you:    { us: { w: 'jə',   s: 'ju'   }, gb: { w: 'jə',   s: 'juː'  } },
    your:   { us: { w: 'jɚ',   s: 'jʊr'  }, gb: { w: 'jə',   s: 'jɔː'  } }
  };

  /* Sound-initial test for the "to/the/do + vowel" rule. Spelling is a poor
   * proxy for the first PHONEME, so the regular traps are listed rather than
   * guessed — silent h (hour, honest), consonantal u/eu (union, euro, use),
   * and one/once, which begin with /w/. */
  var VOWEL_INITIAL_EXCEPT = set('hour hours honest honestly honour honor honours honors ' +
                                 'heir heirs university universe unit units union unions ' +
                                 'used useful use user users unique uniform european euro ' +
                                 'one once eucalyptus ukulele');
  function startsWithVowelSound(word) {
    var w = norm(word);
    if (!w) return false;
    if (VOWEL_INITIAL_EXCEPT[w]) return /^h/.test(w);          // hour → vowel, union → consonant
    return /^[aeiou]/.test(w);
  }

  /* ── Token helpers ───────────────────────────────────────────────────────
   * `tokens` is the LangIPA.phrase() shape: {token} words, {token,space:true},
   * {token,punct:true}. Everything below works on that array directly so
   * callers never have to re-tokenise. */
  function words(tokens) {
    var out = [];
    for (var i = 0; i < tokens.length; i++) {
      if (!tokens[i].space && !tokens[i].punct) out.push(i);
    }
    return out;
  }

  /* Punctuation strong enough to end a thought group. */
  function isBreak(tok) {
    return !!tok && tok.punct && /[.,;:!?—–…]|--/.test(tok.token);
  }
  function isFinalBreak(tok) {
    return !!tok && tok.punct && /[.!?…]/.test(tok.token);
  }

  /* ── 1. content vs function ──────────────────────────────────────────────
   * Returns, for every token index, one of:
   *   'content'  — lexical word, stressed
   *   'function' — closed-class, unstressed by default
   * plus a `role` naming the reading that was chosen, so a UI can explain it.
   */
  function classify(tokens) {
    var idx = words(tokens), out = {};
    for (var k = 0; k < idx.length; k++) {
      var i = idx[k], w = norm(tokens[i].token);
      var prev = k > 0 ? norm(tokens[idx[k - 1]].token) : '';
      var next = k + 1 < idx.length ? norm(tokens[idx[k + 1]].token) : '';
      var nextTok = k + 1 < idx.length ? idx[k + 1] : -1;
      var groupEnd = nextTok < 0 || anyBreakBetween(tokens, i, nextTok);
      out[i] = decide(w, prev, next, groupEnd, k === 0);
    }
    return out;
  }

  function anyBreakBetween(tokens, a, b) {
    for (var i = a + 1; i < b; i++) if (isBreak(tokens[i])) return true;
    return false;
  }

  function decide(w, prev, next, groupEnd, isFirst) {
    if (!w) return { type: 'content', role: 'word' };
    if (ALWAYS_STRONG[w]) return { type: 'content', role: 'emphatic' };

    /* Demonstratives: "That is the book that I read" holds BOTH readings —
     * a pointing demonstrative (stressed /ðæt/) and a relativiser (weak /ðət/).
     * Without a POS tagger the split rests on position, which decides the two
     * readings that actually occur:
     *   · opening a group, or standing at its end  → pointing  → stressed
     *   · anywhere else                            → linking / determiner
     * The residual ambiguity is real ("I know that is true"), so the caller
     * shows the chosen role and lets the user flip it. */
    if (w === 'that' || w === 'this' || w === 'these' || w === 'those') {
      if (isFirst || !prev) return { type: 'content', role: 'demonstrative' };
      if (groupEnd) return { type: 'content', role: 'demonstrative' };
      if (w === 'that') return { type: 'function', role: 'relativiser' };
      return { type: 'function', role: 'determiner' };
    }
    /* Existential "there is / there are" is a placeholder and reduces; the
     * adverb of place ("over there") is a content word. */
    if (w === 'there') {
      if ((isFirst || !prev) && AUX[next]) return { type: 'function', role: 'existential' };
      return { type: 'content', role: 'adverb of place' };
    }
    /* A stranded auxiliary keeps its strong form and takes stress:
     * "Yes, I CAN." / "I don't know where she IS." */
    if (AUX[w] && groupEnd) return { type: 'content', role: 'stranded auxiliary' };
    /* Main-verb "be/have/do" (nothing after it in the group, or followed by a
     * non-verb) still behaves as an operator — keep it a function word unless
     * stranded, which the branch above already caught. */
    if (AUX[w]) return { type: 'function', role: 'auxiliary' };
    if (ARTICLE[w]) return { type: 'function', role: 'article' };
    /* A preposition stranded at the end of a group is stressed:
     * "What are you looking AT?" */
    if (PREP[w]) {
      if (groupEnd) return { type: 'content', role: 'stranded preposition' };
      return { type: 'function', role: 'preposition' };
    }
    /* A group-final pronoun is NOT promoted to a content word — "I like it"
     * keeps the accent on the verb. It does lose its weak form (no weak form
     * survives utterance-finally), and strongReason() handles that separately:
     * "unstressed" and "reduced" are two different questions. */
    if (PRONOUN[w]) return { type: 'function', role: 'pronoun' };
    if (CONJ[w]) return { type: 'function', role: 'conjunction' };
    if (DET[w]) return { type: 'function', role: 'determiner' };
    return { type: 'content', role: 'word' };
  }

  /* ── 2. weak forms ───────────────────────────────────────────────────────
   * weakForm(word, i, tokens, accent, cls) → {ipa, weak:boolean, why} | null
   * `null` means the word has no documented weak form at all. */
  function weakForm(word, i, tokens, accent, cls) {
    var w = norm(word);
    var entry = WEAK[w];
    if (!entry) return null;
    var acc = accent === 'gb' ? 'gb' : 'us';
    var pair = entry[acc];

    /* STRONG positions — the whole point of the feature. */
    var reason = strongReason(w, i, tokens, cls);
    if (reason) return { ipa: pair.s, weak: false, why: reason };

    /* to / the / do take their "pre-vowel" shape before a vowel SOUND. */
    if (entry.preVowel) {
      var nxt = nextWord(tokens, i);
      if (nxt && startsWithVowelSound(nxt)) {
        return { ipa: entry.preVowel[acc], weak: true, why: 'before a vowel' };
      }
    }
    return { ipa: pair.w, weak: true, why: '' };
  }

  function nextWord(tokens, i) {
    for (var j = i + 1; j < tokens.length; j++) {
      if (tokens[j].space) continue;
      if (tokens[j].punct) return null;   // a pause intervenes
      return tokens[j].token;
    }
    return null;
  }

  function strongReason(w, i, tokens, cls) {
    /* end of the utterance or before any pause */
    var j = i + 1, sawBreak = false;
    for (; j < tokens.length; j++) {
      if (tokens[j].space) continue;
      if (tokens[j].punct) { sawBreak = isBreak(tokens[j]); break; }
      break;
    }
    if (j >= tokens.length) return 'at the end';
    if (sawBreak) return 'before a pause';
    /* the classifier already promoted stranded operators to content words */
    if (cls && cls[i] && cls[i].type === 'content') return cls[i].role;
    return '';
  }

  /* ── 3. thought groups + nucleus ─────────────────────────────────────────
   * groups(tokens) → [{ from, to, wordIdx:[…], nucleus }] where `nucleus` is a
   * token index (or -1 for a group with no content word). */
  function groups(tokens) {
    var cls = classify(tokens);
    var out = [], cur = null;
    for (var i = 0; i < tokens.length; i++) {
      if (!cur) cur = { from: i, to: i, wordIdx: [], nucleus: -1 };
      cur.to = i;
      if (!tokens[i].space && !tokens[i].punct) cur.wordIdx.push(i);
      if (isBreak(tokens[i]) && cur.wordIdx.length) { out.push(cur); cur = null; }
    }
    if (cur && cur.wordIdx.length) out.push(cur);
    for (var g = 0; g < out.length; g++) out[g].nucleus = pickNucleus(out[g], tokens, cls);
    return { groups: out, classes: cls };
  }

  /* Default (unmarked) tonicity: the LAST content word of the group. Two
   * exceptions every pronunciation course teaches:
   *   · a sentence-final phrasal-verb particle takes it — "turn it OFF";
   *   · an empty pro-form hands it back — "I don't LIKE it". */
  function pickNucleus(group, tokens, cls) {
    var wi = group.wordIdx;
    if (!wi.length) return -1;

    var last = wi[wi.length - 1], lastW = norm(tokens[last].token);
    if (PARTICLE[lastW] && wi.length > 1) {
      var before = norm(tokens[wi[wi.length - 2]].token);
      // "turn it off" / "put them on" — particle after an object pronoun,
      // and "get up" / "sit down" — particle straight after a verb.
      if (PRONOUN[before] || (cls[wi[wi.length - 2]] || {}).type === 'content') return last;
    }

    for (var k = wi.length - 1; k >= 0; k--) {
      var i = wi[k], w = norm(tokens[i].token);
      if ((cls[i] || {}).type !== 'content') continue;
      // an empty pro-form at the very end passes the nucleus leftwards
      if (k === wi.length - 1 && EMPTY_END[w] && wi.length > 1) continue;
      return i;
    }
    return wi[wi.length - 1];
  }

  /* ── 4. tune: does the nucleus fall or rise? ─────────────────────────────
   * The other half of what a sentence-stress lesson teaches. The default
   * tunes are regular enough to state from punctuation and the opening word:
   *   · a wh-question falls  — "Where are you GOing?" ↘
   *   · a yes/no question rises — "Are you COMing?" ↗
   *   · a non-final group rises, holding the turn — "If you LIKE ↗, we can GO ↘"
   *   · everything else falls: statements, commands, exclamations.
   * Returns 'fall' | 'rise'. */
  var WH = set('what which who whom whose why how where when');
  function tuneOf(group, tokens, isLast) {
    var end = null;
    for (var i = group.to; i >= group.from; i--) {
      if (tokens[i] && tokens[i].punct) { end = tokens[i].token; break; }
      if (tokens[i] && !tokens[i].space) break;
    }
    if (end && end.indexOf('?') >= 0) {
      var first = group.wordIdx.length ? norm(tokens[group.wordIdx[0]].token) : '';
      return WH[first] ? 'fall' : 'rise';
    }
    if (!isLast) return 'rise';          // more to come — the voice stays up
    return 'fall';
  }

  return {
    classify: classify,
    weakForm: weakForm,
    groups: groups,
    tuneOf: tuneOf,
    startsWithVowelSound: startsWithVowelSound,
    hasWeakForm: function (w) { return !!WEAK[norm(w)]; },
    isBreak: isBreak,
    isFinalBreak: isFinalBreak,
    PARTICLE: PARTICLE
  };
});
